import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import { ProjetsService } from 'app/services/projets/projets.service';
import { AwscontrolService, UploadResponse, DeleteResponse } from 'app/services/aws/awscontrol.service';
import { Location } from '@angular/common';
import Swal from 'sweetalert2';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { MatSelect } from '@angular/material/select';

@Component({
  selector: 'app-ajouter-projet',
  templateUrl: './ajouter-projet.component.html',
  styleUrls: ['./ajouter-projet.component.scss']
})
export class AjouterProjetComponent implements OnInit {
  @ViewChild('agencySelect') agencySelect!: MatSelect;
  @ViewChild('agencySearchInput') agencySearchInput!: ElementRef;

  projectForm: FormGroup;
  isLoading = false;
  
  selectedFiles: FileList;
  uploadedImages: string[] = [];
  
  allAgencies: any[] = [];
  filteredAgencies: any[] = [];
  categories: string[] = [];
  categoryInput: string = '';
  
  // Store the selected agency ID for navigation
  selectedAgencyId: string | null = null;
  
  statusOptions = [
    { value: 'DISPONIBLE', label: 'Disponible', color: '#4CAF50' },
    { value: 'EN COURS', label: 'En cours', color: '#2196F3' },
    { value: 'VENDU', label: 'Vendu', color: '#F44336' }
  ];

  uploadProgress: number = 0;
  isUploading: boolean = false;

  agencySearchTerm: string = '';
  isAgencyDropdownOpen: boolean = false;

  constructor(
    private location: Location,
    private projetService: ProjetsService,
    private router: Router,
    private agenciesService: AgenciesService,
    private awsService: AwscontrolService,
    private fb: FormBuilder
  ) {
    this.initForm();
  }

  initForm(): void {
    this.projectForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      description: ['', [Validators.required, Validators.minLength(10)]],
      adresse: ['', Validators.maxLength(200)],
      agencyName: ['', Validators.required],
      status: ['DISPONIBLE', Validators.required],
      videoUrlProjet: ['', this.urlValidator],
      nomCommercial: ['', Validators.maxLength(50)],
      numCommercial: ['', Validators.pattern('^[0-9+\\s-]*$')],
      emailCommercial: ['', Validators.email],
      categoryList: [[]]
    });
  }

  urlValidator(control: AbstractControl): { [key: string]: boolean } | null {
    if (!control.value || control.value === '') return null;
    try {
      new URL(control.value);
      return null;
    } catch {
      return { invalidUrl: true };
    }
  }

  ngOnInit(): void {
    this.loadAgencies();
    this.clearStoredImages();
  }

  loadAgencies(): void {
    this.isLoading = true;
    this.agenciesService.getList().subscribe({
      next: (data: any) => {
        let agencies = [];
        if (Array.isArray(data)) agencies = data;
        else if (data) agencies = [data];
        
        this.allAgencies = agencies
          .filter(agency => agency && agency.name && typeof agency.name === 'string' && agency.role === 'promoteur-immobilieres')
          .map(agency => ({
            id: agency.id,
            name: agency.name.trim(),
            role: agency.role,
            initial: agency.name.trim().charAt(0).toUpperCase(),
            color: this.getRandomColor()
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        
        this.filteredAgencies = [...this.allAgencies];
        this.isLoading = false;
        
        if (this.allAgencies.length === 0) {
          Swal.fire({
            icon: 'info',
            title: 'Aucun promoteur disponible',
            text: 'Aucune agence avec le rôle "promoteur immobilier" n\'a été trouvée'
          });
        }
      },
      error: (error) => {
        console.error('Erreur lors du chargement des agences:', error);
        this.allAgencies = [];
        this.filteredAgencies = [];
        this.isLoading = false;
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Impossible de charger la liste des promoteurs'
        });
      }
    });
  }

  private getRandomColor(): string {
    const colors = ['#FF6B6B', '#4ECDC4', '#FFD166', '#06D6A0', '#118AB2', '#EF476F', '#7B68EE', '#20B2AA', '#FFA07A', '#9370DB'];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  onAgencySelectOpened(): void {
    this.isAgencyDropdownOpen = true;
    this.agencySearchTerm = '';
    this.filterAgencies();
    setTimeout(() => {
      if (this.agencySearchInput) this.agencySearchInput.nativeElement.focus();
    }, 100);
  }

  onAgencySelectClosed(): void {
    this.isAgencyDropdownOpen = false;
    this.agencySearchTerm = '';
    this.filterAgencies();
  }

  filterAgencies(): void {
    if (!this.agencySearchTerm || this.agencySearchTerm.trim() === '') {
      this.filteredAgencies = [...this.allAgencies];
    } else {
      const term = this.agencySearchTerm.toLowerCase().trim();
      this.filteredAgencies = this.allAgencies.filter(agency => agency.name.toLowerCase().includes(term));
    }
  }

  clearAgencySearch(): void {
    this.agencySearchTerm = '';
    this.filterAgencies();
    if (this.agencySearchInput) this.agencySearchInput.nativeElement.focus();
  }

  selectAgency(agency: any): void {
    this.projectForm.patchValue({ agencyName: agency.name });
    this.selectedAgencyId = agency.id; // store the ID
    this.agencySelect.close();
  }

  // Image upload functions
  onProjectImagesSelected(event: any): void {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    this.selectedFiles = files;
    this.uploadProjectImages();
  }

  uploadProjectImages(): void {
    if (!this.selectedFiles) return;
    this.isUploading = true;
    const filesArray: File[] = Array.from(this.selectedFiles);
    const renamedFiles = filesArray.map(file => {
      const timestamp = Date.now();
      const randomString = Math.random().toString(36).substring(2, 8);
      const newName = `project-images/${timestamp}_${randomString}_${file.name}`;
      return new File([file], newName, { type: file.type });
    });

    this.awsService.uploadFiles(renamedFiles, 'project-images').subscribe({
      next: (response: UploadResponse) => {
        this.isUploading = false;
        this.uploadProgress = 0;
        if (response.success) {
          this.uploadedImages.push(...response.urls);
          Swal.fire({
            icon: 'success',
            title: 'Images téléchargées',
            text: `${response.count} image(s) ont été téléchargées avec succès`,
            timer: 2000,
            showConfirmButton: false
          });
        } else {
          Swal.fire({ icon: 'error', title: 'Erreur', text: response.error || 'Échec du téléchargement des images' });
        }
      },
      error: (err) => {
        this.isUploading = false;
        this.uploadProgress = 0;
        console.error('Error uploading images:', err);
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de télécharger les images. Veuillez réessayer.' });
      }
    });
  }

  moveImageUp(index: number): void {
    if (index === 0) return;
    const temp = this.uploadedImages[index];
    this.uploadedImages[index] = this.uploadedImages[index - 1];
    this.uploadedImages[index - 1] = temp;
  }

  moveImageDown(index: number): void {
    if (index === this.uploadedImages.length - 1) return;
    const temp = this.uploadedImages[index];
    this.uploadedImages[index] = this.uploadedImages[index + 1];
    this.uploadedImages[index + 1] = temp;
  }

  setPrimaryImage(index: number): void {
    if (index === 0) return;
    const image = this.uploadedImages[index];
    this.uploadedImages.splice(index, 1);
    this.uploadedImages.unshift(image);
    Swal.fire({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 1500,
      icon: 'success',
      title: 'Image principale définie'
    });
  }

  removeProjectImage(index: number): void {
    const imageUrl = this.uploadedImages[index];
    Swal.fire({
      title: 'Supprimer cette image ?',
      text: 'Cette action est irréversible',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Supprimer',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.awsService.deleteFile(imageUrl).subscribe({
          next: (response: DeleteResponse) => {
            if (response.status === 'success') {
              this.uploadedImages.splice(index, 1);
              Swal.fire({ icon: 'success', title: 'Image supprimée', text: 'L\'image a été supprimée avec succès', timer: 1500, showConfirmButton: false });
            } else {
              Swal.fire({ icon: 'error', title: 'Erreur', text: response.error || 'Échec de la suppression de l\'image' });
            }
          },
          error: (err) => {
            console.error('Error deleting image:', err);
            Swal.fire({ icon: 'error', title: 'Erreur', text: 'Échec de la suppression de l\'image. Veuillez réessayer.' });
          }
        });
      }
    });
  }

  addCategory(): void {
    if (this.categoryInput && this.categoryInput.trim() !== '') {
      const category = this.categoryInput.trim();
      if (!this.categories.includes(category)) {
        this.categories.push(category);
        this.projectForm.patchValue({ categoryList: this.categories });
      }
      this.categoryInput = '';
    }
  }

  removeCategory(category: string): void {
    const index = this.categories.indexOf(category);
    if (index >= 0) {
      this.categories.splice(index, 1);
      this.projectForm.patchValue({ categoryList: this.categories });
    }
  }

  onSubmit(): void {
    if (this.projectForm.invalid) {
      this.markFormGroupTouched(this.projectForm);
      const invalidControl = document.querySelector('.ng-invalid');
      if (invalidControl) invalidControl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      Swal.fire({ icon: 'error', title: 'Formulaire invalide', text: 'Veuillez corriger les erreurs dans le formulaire' });
      return;
    }

    if (this.uploadedImages.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Images requises', text: 'Veuillez ajouter au moins une image pour le projet' });
      return;
    }

    const formValue = this.projectForm.value;
    const projectData = {
      ...formValue,
      photos: this.uploadedImages,
      categoryList: this.categories,
      createdAt: new Date().toISOString()
    };

    this.isLoading = true;
    this.projetService.createProjet(projectData).subscribe({
      next: (response) => {
        this.isLoading = false;
        Swal.fire({
          icon: 'success',
          title: 'Projet créé !',
          text: 'Le projet a été créé avec succès',
          showConfirmButton: false,
          timer: 2000
        }).then(() => {
          this.clearForm();
          // Navigate to the list of projects for the selected agency
          if (this.selectedAgencyId) {
            this.router.navigate(['/list-projet', this.selectedAgencyId]);
          } else {
            // Fallback: go back to previous page if agency ID is missing
            this.location.back();
          }
        });
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error creating project:', error);
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Une erreur est survenue lors de la création du projet' });
      }
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) this.markFormGroupTouched(control);
    });
  }

  clearForm(): void {
    this.projectForm.reset({ status: 'DISPONIBLE' });
    this.uploadedImages = [];
    this.categories = [];
    this.selectedAgencyId = null;
    this.clearStoredImages();
  }

  clearStoredImages(): void {
    localStorage.removeItem('projImg');
  }

  getFieldError(fieldName: string): string {
    const field = this.projectForm.get(fieldName);
    if (!field || !field.errors || !field.touched) return '';
    if (field.errors['required']) return 'Ce champ est requis';
    if (field.errors['minlength']) return `Minimum ${field.errors['minlength'].requiredLength} caractères`;
    if (field.errors['maxlength']) return `Maximum ${field.errors['maxlength'].requiredLength} caractères`;
    if (field.errors['email']) return 'Email invalide';
    if (field.errors['pattern']) return 'Format invalide';
    if (field.errors['invalidUrl']) return 'URL invalide';
    return '';
  }

  navigateBack(): void {
    this.location.back();
  }

  getCurrentFormData(): any {
    return this.projectForm.value;
  }

  getStatusColor(status: string): string {
    const statusOption = this.statusOptions.find(opt => opt.value === status);
    return statusOption ? statusOption.color : '#757575';
  }
}